import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from '../AdminPortal.module.scss';
import { FancyButton } from '../../../components/primitives/FancyButton';
import { useM2MApiContext } from '../../../api/m2mApi';
import { TpOrganizationInfo } from '../../../api/models/admin';
import { useUser } from '../../../app/hooks';
import { promptAddUserToOrganization, promptCreateUser, promptDeleteUser } from '../actions/m2mCrudUser';
import { TpUserInfo } from '../../../data/interfaces';
import { UserOrganizations } from './UserOrganizations';

interface TpAdminOrganizationsProps {
  users: TpUserInfo[];
  organizations: TpOrganizationInfo[];
  updateUsers: () => void;
  updateOrganizations: () => void;
}

const AdminUsers = (props: TpAdminOrganizationsProps) => {
  const { users, organizations, updateUsers, updateOrganizations } = props;
  const m2MApi = useM2MApiContext();
  const { user } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  const [expandedUser, setExpandedUser] = useState<string | null>(null);

  const handleToggleExpand = (userEmail: string) => {
    setExpandedUser((prevMail) => (prevMail === userEmail ? null : userEmail));
  };

  const toggleLoading = (enable: boolean, message: string = '') => {
    setIsLoading(enable);
    setLoadingMessage(message);
  };

  const handleCreateUser = () => {
    setIsLoading(true);
    setLoadingMessage('Creating User ...');
    promptCreateUser(m2MApi, organizations)
      .then((newUser) => {
        setIsLoading(false);
        setLoadingMessage('');
        updateUsers();
        updateOrganizations();
      })
      .catch((err) => {
        setIsLoading(false);
        setLoadingMessage('');
      });
  };

  const handleDeleteAction = (usr: TpUserInfo) => {
    setIsLoading(true);
    setLoadingMessage('Deleting user...');
    promptDeleteUser(m2MApi, usr)
      .then((newUser) => {
        setIsLoading(false);
        setLoadingMessage('');
        updateUsers();
        updateOrganizations();
      })
      .catch((err) => {
        setIsLoading(false);
        setLoadingMessage('');
      });
  };
  const handleAddUserToOrganizationAction = (usr: TpUserInfo) => {
    setIsLoading(true);
    setLoadingMessage('Adding User to Organization...');
    const possibleOrganizations = organizations.filter((org) => {
      return !usr.organizations.map((o) => o.organizationId).includes(org.identifier!);
    });
    promptAddUserToOrganization(m2MApi, usr, possibleOrganizations)
      .then((newUser) => {
        setIsLoading(false);
        setLoadingMessage('');
        updateUsers();
        updateOrganizations();
      })
      .catch((err) => {
        setIsLoading(false);
        setLoadingMessage('');
      });
  };

  return (
    <>
      <div style={{ paddingTop: '25px' }}>
        <FancyButton iconName="fas:user-plus" onClick={handleCreateUser}>
          Create New User
        </FancyButton>

        <h2 className={styles.subtitle}>Overview ({users.length})</h2>
        {isLoading && (
          <div className={styles.loadingBox}>
            <span className={styles.loadingMessage}>{loadingMessage}</span>
          </div>
        )}
        {!!users && users.length > 0 && (
          <div className={styles.organizationList}>
            {users.map((userInfo: TpUserInfo) => (
              <div
                key={userInfo.email}
                className={`${styles.organizationItem} ${expandedUser === userInfo.email ? styles.expanded : ''}`}
              >
                <div className={styles.organizationHeader}>
                  <div className={styles.organizationInfo}>
                    <span className={styles.organizationName}>
                      {userInfo.email}
                      {!!user && userInfo.email === user.email && (
                        <span className={styles.activeOrganization}> (Currently logged in)</span>
                      )}
                    </span>
                  </div>
                  {!!userInfo.email && (
                    <div>
                      <FontAwesomeIcon
                        icon={expandedUser === userInfo.email ? 'minimize' : 'maximize'}
                        className={styles.actionIcon}
                        title={expandedUser === userInfo.email ? 'Collapse' : 'Expand'}
                        onClick={() => handleToggleExpand(userInfo.email!)}
                      />
                    </div>
                  )}
                </div>
                {expandedUser === userInfo.email && (
                  <div className={styles.expandedContent}>
                    <div className={styles.organizationActionBlock}>
                      <FancyButton iconName="fas:user-plus" onClick={() => handleAddUserToOrganizationAction(userInfo)}>
                        Add User to Organization
                      </FancyButton>
                      <FancyButton iconName="fas:trash-can" onClick={() => handleDeleteAction(userInfo)}>
                        Delete User
                      </FancyButton>
                    </div>
                    <UserOrganizations
                      users={users}
                      organizations={organizations}
                      selectedUser={userInfo.email}
                      allowModifyUser={true}
                      allowRemoveUser={true}
                      allowDeleteUser={false}
                      setIsLoading={toggleLoading}
                      updateUsers={updateUsers}
                      updateOrganizations={updateOrganizations}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default AdminUsers;
